import { Validators, ValidatorFn } from '@angular/forms';
import { FourYearOldAttestationStatus, StudentType } from '@k12/models';
import type { RecordSection, FieldOption } from '@k12/app-components';

/** Rejects negative currency entries. Blank is left alone — these amounts are optional. */
const nonNegativeAmount: ValidatorFn = Validators.min(0);


export const SOS_SECTIONS: RecordSection[] = [
  {
    title: 'Application Information',
    fields: [
      { key: 'disclosedInformation', label: 'Income Disclosed', type: 'boolean' },
      {
        key: 'householdIncome',
        label: 'Household Income',
        type: 'currency',
        validators: [Validators.required, Validators.min(0)]
      },
      {
        // Written only by the Income Verification process, so it stays readonly here and renders
        // as an em dash until IV completes.
        key: 'verifiedIncome',
        label: 'Verified Income',
        type: 'currency',
        readonly: true
      },
      {
        key: 'householdMemberCount',
        label: 'Household Size',
        type: 'number',
        validators: [Validators.required, Validators.min(1)]
      },
      { key: 'isFoster', label: 'Foster Status', type: 'boolean' },
      { key: 'dhhsRelease', label: 'DHHS Release', type: 'boolean', showWhen: { field: 'isFoster', is: true } },
      { key: 'dhhsCaseManager', label: 'DHHS Case Manager', type: 'text', showWhen: { field: 'isFoster', is: true } },
      {
        key: 'dhhsCounty',
        label: 'DHHS County',
        type: 'select',
        showWhen: { field: 'isFoster', is: true },
        optionsKey: 'ncCounties'
      },
      { key: 'chosenForIncomeVerification', label: 'Chosen for Income Verification', type: 'boolean' },
      { key: 'studentType', label: 'Student Type', type: 'select', optionsKey: 'studentTypes' },
      {
        key: 'fourYearOldAttestationStatus',
        label: '4 Year Old Attestation',
        type: 'select',
        optionsKey: 'fourYearOldAttestationStatus'
      },
      { 
        key: 'osIneligibilityReasonId', 
        label: 'Ineligibility Reason', 
        type: 'select', 
        optionsKey: 'inEligibilityReasons',
        editableBy: ['admin'] 
      }
    ]
  },
  {
    title: 'Scholarship Information',
    fields: [
      { key: 'osAwardAmountOffered', label: 'Annual Award Amount', type: 'currency', validators: [nonNegativeAmount] },
      { key: 'osTier', label: 'Award Tier', type: 'number' }
    ]
  },
  {
    title: 'Financial Details',
    fields: [
      { key: 'fallOSAwardAmount', label: 'Fall Award Amount', type: 'currency', validators: [nonNegativeAmount] },
      { key: 'springOSAwardAmount', label: 'Spring Award Amount', type: 'currency', validators: [nonNegativeAmount] },
      {
        key: 'endorsedByParentFall',
        label: 'Fall Term Endorsed by Parent',
        type: 'boolean',
        validators: [Validators.required]
      },
      {
        key: 'endorsedByParentSpring',
        label: 'Spring Term Endorsed by Parent',
        type: 'boolean',
        validators: [Validators.required]
      },
      { key: 'osFallTuitionAmount', label: 'Fall Tuition to be Paid', type: 'currency', validators: [nonNegativeAmount] },
      { key: 'osSpringTuitionAmount', label: 'Spring Tuition to be Paid', type: 'currency', validators: [nonNegativeAmount] },
      { key: 'osFallRemainingAmount', label: 'Remaining Fall Funds', type: 'currency', validators: [nonNegativeAmount] },
      { key: 'osSpringRemainingAmount', label: 'Remaining Spring Funds', type: 'currency', validators: [nonNegativeAmount] },
      { key: 'osFallDisbursedAmount', label: 'Fall Disbursed Amount', type: 'currency', readonly: true },
      { key: 'osSpringDisbursedAmount', label: 'Spring Disbursed Amount', type: 'currency', readonly: true }
    ]
  }
];

export const SOS_OPTIONS: Record<string, FieldOption[]> = {
  yesNo: [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ],
  studentTypes: [
    { label: 'New', value: StudentType.New },
    { label: 'Renewal', value: StudentType.Renewal },
    { label: 'Pending Renewal', value: StudentType.PendingRenewal },
    { label: 'Rejected Renewal', value: StudentType.RejectedRenewal }
  ],
  fourYearOldAttestationStatus: [
    { label: 'Not Required', value: FourYearOldAttestationStatus.NotRequired },
    { label: 'Required', value: FourYearOldAttestationStatus.Required },
    { label: 'Completed', value: FourYearOldAttestationStatus.Completed }
  ],
  ncCounties: [],
  inEligibilityReasons: []
};
